/**
 * AuthProvider — loads `/auth/me` once at mount, listens for the global
 * "auth:expired" event from the fetch wrapper, and exposes `useAuth`.
 */

import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { api, ensureCsrf, ApiError, AUTH_EXPIRED_EVENT } from '@/api/client';
import { keys } from '@/api/queryKeys';
import type { MeResponse } from '@/api/types';

interface AuthCtx {
  user: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Bootstrap CSRF cookie once. Doesn't matter if it 404s; the cookie is the
  // important side effect.
  useEffect(() => {
    ensureCsrf().catch(() => undefined);
  }, []);

  const { data, isLoading } = useQuery<MeResponse | null>({
    queryKey: keys.auth.me,
    queryFn: async () => {
      try {
        return await api<MeResponse>('/auth/me', { silentUnauth: true });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Global 401 listener — any protected request that fails with 401 lands us here.
  useEffect(() => {
    function onExpired() {
      qc.setQueryData(keys.auth.me, null);
      const onAuthPage = ['/login', '/register', '/verify-email', '/reset-password'].some((p) =>
        location.pathname.startsWith(p),
      );
      if (!onAuthPage) {
        navigate('/login', { replace: true });
      }
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [navigate, qc, location.pathname]);

  const logout = useMutation({
    mutationFn: () => api('/auth/logout', { method: 'POST' }),
    onSettled: async () => {
      qc.setQueryData(keys.auth.me, null);
      await qc.invalidateQueries();
      navigate('/login', { replace: true });
    },
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: keys.auth.me });
  }, [qc]);

  const value = useMemo<AuthCtx>(
    () => ({
      user: data ?? null,
      loading: isLoading,
      refresh,
      logout: async () => {
        await logout.mutateAsync();
      },
    }),
    [data, isLoading, refresh, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
